import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route } from 'react-router-dom'
import { Toaster } from 'sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { AppShell } from '@/components/layout/AppShell'
import { RequireAuth } from '@/components/auth/RequireAuth'
import { useAuthStore } from '@/stores/authStore'
import { disableUserSettingsSync, initializeUserSettingsSync } from '@/stores/settingsStore'
import { useTheme } from '@/hooks/useTheme'
import { useLanguage } from '@/hooks/useLanguage'
import { initKeycloak, getProfile, isAuthenticated } from '@/lib/keycloak'
import { userApi } from '@/lib/api'

const HomePage = lazy(() => import('@/pages/HomePage'))
const WelcomePage = lazy(() => import('@/pages/WelcomePage'))
const ExtensionPage = lazy(() => import('@/pages/ExtensionPage'))
const VaultsPage = lazy(() => import('@/pages/VaultsPage'))
const VaultDetailPage = lazy(() => import('@/pages/VaultDetailPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const LegalPage = lazy(() => import('@/pages/LegalPage'))
const SupportPage = lazy(() => import('@/pages/SupportPage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

function PageFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
      <span className="animate-pulse">...</span>
    </div>
  )
}

function LazyPage({ children }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>
}

export default function App() {
  useTheme()
  useLanguage()
  const { setReady, setAuth } = useAuthStore()

  useEffect(() => {
    let cancelled = false
    initKeycloak()
      .then(() => {
        if (cancelled) return
        const authed = isAuthenticated()
        const profile = getProfile()
        setAuth(authed, profile)
        setReady(true)
        if (authed) {
          initializeUserSettingsSync(profile?.sub).catch((err) => console.error('Settings sync init failed', err))
          userApi.pingConnection().catch(() => {})
        } else {
          disableUserSettingsSync()
        }
      })
      .catch((err) => {
        console.error('Keycloak init failed', err)
        if (!cancelled) { setAuth(false, null); setReady(true); disableUserSettingsSync() }
      })
    return () => { cancelled = true }
  }, [setAuth, setReady])

  return (
    <TooltipProvider delayDuration={300}>
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/" element={<LazyPage><HomePage /></LazyPage>} />
          <Route path="/welcome" element={<LazyPage><WelcomePage /></LazyPage>} />
          <Route path="/extension" element={<LazyPage><ExtensionPage /></LazyPage>} />
          <Route path="/vaults" element={<RequireAuth><LazyPage><VaultsPage /></LazyPage></RequireAuth>} />
          <Route path="/vaults/:vaultUuid" element={<RequireAuth><LazyPage><VaultDetailPage /></LazyPage></RequireAuth>} />
          <Route path="/settings" element={<RequireAuth><LazyPage><SettingsPage /></LazyPage></RequireAuth>} />
          <Route path="/legal" element={<LazyPage><LegalPage /></LazyPage>} />
          <Route path="/legal/terms" element={<LazyPage><LegalPage /></LazyPage>} />
          <Route path="/legal/privacy" element={<LazyPage><LegalPage /></LazyPage>} />
          <Route path="/legal/privacy-extension" element={<LazyPage><LegalPage /></LazyPage>} />
          <Route path="/privacy-extension" element={<LazyPage><LegalPage /></LazyPage>} />
          <Route path="/legal/licenses" element={<LazyPage><LegalPage /></LazyPage>} />
          <Route path="/legal/versions" element={<LazyPage><LegalPage /></LazyPage>} />
          <Route path="/legal/third-party-notices" element={<LazyPage><LegalPage /></LazyPage>} />
          <Route path="/support" element={<LazyPage><SupportPage /></LazyPage>} />
          <Route path="*" element={<LazyPage><NotFoundPage /></LazyPage>} />
        </Route>
      </Routes>
      <Toaster richColors position="top-right" />
    </TooltipProvider>
  )
}
